import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AdminLayout } from "./components/AdminLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleRoute } from "./components/RoleRoute";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { BannersPage } from "./pages/BannersPage";
import { CouponsPage } from "./pages/CouponsPage";
import { CustomersPage } from "./pages/CustomersPage";
import { DashboardPage } from "./pages/DashboardPage";
import { FlashSalesPage } from "./pages/FlashSalesPage";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { InventoryPage } from "./pages/InventoryPage";
import { LoginPage } from "./pages/LoginPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { OrdersPage } from "./pages/OrdersPage";
import { ProductsPage } from "./pages/ProductsPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { ReturnsPage } from "./pages/ReturnsPage";
import { ReviewsPage } from "./pages/ReviewsPage";
import { SeoPage } from "./pages/SeoPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ShippingPage } from "./pages/ShippingPage";
import { UsersPage } from "./pages/UsersPage";
import { CartPage } from "./pages/CartPage";
// Master pages
import { CreateCategoryPage } from "./pages/master/CreateCategoryPage";
import { CreateSubCategoryPage } from "./pages/master/CreateSubCategoryPage";
import { CreateBrandPage } from "./pages/master/CreateBrandPage";
import { CreateMaterialPage } from "./pages/master/CreateMaterialPage";
import { CreateBlogPage } from "./pages/master/CreateBlogPage";
import { CreateMaterialTypePage } from "./pages/master/CreateMaterialTypePage";
import { CreateStatusPage } from "./pages/master/CreateStatusPage";
import { CreateStoneTypePage } from "./pages/master/CreateStoneTypePage";
import { CreateOccasionTypePage } from "./pages/master/CreateOccasionTypePage";
import { CreateProductTagPage } from "./pages/master/CreateProductTagPage";
import { AddAdvertisementBannerPage } from "./pages/AddAdvertisementBannerPage";
import { QuoteRequestsPage } from "./pages/QuoteRequestsPage";
import { NewsletterPage } from "./pages/NewsletterPage";
import { NavigationPage } from "./pages/NavigationPage";
import { FaqPage } from "./pages/FaqPage";
import { TestimonialsPage } from "./pages/TestimonialsPage";
import { CreateTags } from "./pages/master/CreateTags";
// New pages
import { PaymentsPage } from "./pages/PaymentsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { WishlistPage } from "./pages/WishlistPage";
import { TaxPage } from "./pages/TaxPage";
import { GiftCardsPage } from "./pages/GiftCardsPage";
import { LoyaltyPointsPage } from "./pages/LoyaltyPointsPage";

export function AdminApp() {
  function withShell(page: ReactNode) {
    return (
      <ProtectedRoute>
        <AdminLayout>{page}</AdminLayout>
      </ProtectedRoute>
    );
  }

  return (
    <Routes>
      <Route path="login" element={<LoginPage />} />
      <Route path="register" element={<RegisterPage />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="forbidden" element={<ForbiddenPage />} />
      <Route path="" element={withShell(<Navigate to="/admin/dashboard" replace />)} />

      {/* All roles */}
      <Route path="dashboard" element={withShell(<DashboardPage />)} />
      <Route path="products" element={withShell(<ProductsPage />)} />
      <Route path="orders" element={withShell(<OrdersPage />)} />
      <Route path="customers" element={withShell(<CustomersPage />)} />
      <Route path="analytics" element={withShell(<AnalyticsPage />)} />
      <Route path="notifications" element={withShell(<NotificationsPage />)} />
      <Route path="returns" element={withShell(<ReturnsPage />)} />
      <Route path="reviews" element={withShell(<ReviewsPage />)} />

      {/* Admin only */}
      <Route path="users" element={withShell(<RoleRoute allow={["admin"]}><UsersPage /></RoleRoute>)} />
      <Route path="coupons" element={withShell(<RoleRoute allow={["admin"]}><CouponsPage /></RoleRoute>)} />
      <Route path="settings" element={withShell(<RoleRoute allow={["admin"]}><SettingsPage /></RoleRoute>)} />
      <Route path="banners" element={withShell(<RoleRoute allow={["admin"]}><BannersPage /></RoleRoute>)} />
      <Route path="inventory" element={withShell(<RoleRoute allow={["admin"]}><InventoryPage /></RoleRoute>)} />
      <Route path="flash-sales" element={withShell(<RoleRoute allow={["admin"]}><FlashSalesPage /></RoleRoute>)} />
      <Route path="shipping" element={withShell(<RoleRoute allow={["admin"]}><ShippingPage /></RoleRoute>)} />
      <Route path="carts" element={withShell(<RoleRoute allow={["admin"]}><CartPage /></RoleRoute>)} />
      <Route path="seo" element={withShell(<RoleRoute allow={["admin"]}><SeoPage /></RoleRoute>)} />

      {/* New pages — Admin only */}
      <Route path="payments"   element={withShell(<RoleRoute allow={["admin"]}><PaymentsPage /></RoleRoute>)} />
      <Route path="reports"    element={withShell(<RoleRoute allow={["admin"]}><ReportsPage /></RoleRoute>)} />
      <Route path="wishlists"  element={withShell(<RoleRoute allow={["admin"]}><WishlistPage /></RoleRoute>)} />
      <Route path="tax"        element={withShell(<RoleRoute allow={["admin"]}><TaxPage /></RoleRoute>)} />
      <Route path="gift-cards"    element={withShell(<RoleRoute allow={["admin"]}><GiftCardsPage /></RoleRoute>)} />
      <Route path="loyalty-points" element={withShell(<RoleRoute allow={["admin"]}><LoyaltyPointsPage /></RoleRoute>)} />

      {/* Master — Admin only */}
      <Route path="master" element={withShell(<RoleRoute allow={["admin"]}><Navigate to="/admin/master/create-category" replace /></RoleRoute>)} />
      <Route path="master/create-category"     element={withShell(<RoleRoute allow={["admin"]}><CreateCategoryPage /></RoleRoute>)} />
      <Route path="master/create-sub-category" element={withShell(<RoleRoute allow={["admin"]}><CreateSubCategoryPage /></RoleRoute>)} />
      <Route path="master/create-brand"        element={withShell(<RoleRoute allow={["admin"]}><CreateBrandPage /></RoleRoute>)} />
      <Route path="master/create-material"      element={withShell(<RoleRoute allow={["admin"]}><CreateMaterialPage /></RoleRoute>)} />
      <Route path="master/create-material-type" element={withShell(<RoleRoute allow={["admin"]}><CreateMaterialTypePage /></RoleRoute>)} />
      <Route path="master/create-status"        element={withShell(<RoleRoute allow={["admin"]}><CreateStatusPage /></RoleRoute>)} />
      <Route path="master/create-stone-type"    element={withShell(<RoleRoute allow={["admin"]}><CreateStoneTypePage /></RoleRoute>)} />
      <Route path="master/create-occasion-type" element={withShell(<RoleRoute allow={["admin"]}><CreateOccasionTypePage /></RoleRoute>)} />
      <Route path="master/create-product-tag"   element={withShell(<RoleRoute allow={["admin"]}><CreateProductTagPage /></RoleRoute>)} />
      <Route path="master/create-tags"     element={withShell(<RoleRoute allow={["admin"]}><CreateTags /></RoleRoute>)} />
      <Route path="advertisement-banners"        element={withShell(<RoleRoute allow={["admin"]}><AddAdvertisementBannerPage /></RoleRoute>)} />
      {/* Blog — top level */}
      <Route path="blog" element={withShell(<RoleRoute allow={["admin"]}><CreateBlogPage /></RoleRoute>)} />
      {/* Content pages */}
      <Route path="quote-requests" element={withShell(<RoleRoute allow={["admin"]}><QuoteRequestsPage /></RoleRoute>)} />
      <Route path="newsletter"     element={withShell(<RoleRoute allow={["admin"]}><NewsletterPage /></RoleRoute>)} />
      <Route path="navigation"     element={withShell(<RoleRoute allow={["admin"]}><NavigationPage /></RoleRoute>)} />
      <Route path="faq"            element={withShell(<RoleRoute allow={["admin"]}><FaqPage /></RoleRoute>)} />
      <Route path="testimonials"   element={withShell(<RoleRoute allow={["admin"]}><TestimonialsPage /></RoleRoute>)} />

      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}
